import React, { useMemo } from 'react';
import {
  Box, Paper, Button, TextField, Typography, ButtonGroup,
} from '@mui/material';
import {
  CalendarMonth as CalIcon,
  FileDownload as ExportIcon,
} from '@mui/icons-material';

// ============================================================================
//  DateFilterBar — Reusable filter bar for all report pages.
//
//  Props:
//    value:    { preset, from, to }       (controlled)
//    onChange: (newValue) => void
//    onExport: () => void                 (optional — shows Export Excel button)
//    exportLabel: string                  (default: "Export Excel")
//    extra:    React node                 (optional — render slot for additional filters)
// ============================================================================
const PRESETS = [
  { id: 'today',         label: 'Today' },
  { id: 'yesterday',     label: 'Yesterday' },
  { id: 'this_week',     label: 'This Week' },
  { id: 'last_week',     label: 'Last Week' },
  { id: 'this_month',    label: 'This Month' },
  { id: 'last_month',    label: 'Last Month' },
  { id: 'last_3_months', label: 'Last 3 Months' },
  { id: 'this_year',     label: 'This Year' },
  { id: 'custom',        label: 'Custom' },
];

function fmt(d) { return d.toISOString().slice(0, 10); }

function presetRange(preset, currentFrom, currentTo) {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth(), d = now.getDate();
  const today = fmt(now);

  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday': {
      const dt = new Date(y, m, d - 1);
      return { from: fmt(dt), to: fmt(dt) };
    }
    case 'this_week': {
      const day = now.getDay();
      const monday = new Date(y, m, d - (day === 0 ? 6 : day - 1));
      return { from: fmt(monday), to: today };
    }
    case 'last_week': {
      const day = now.getDay();
      const lastMon = new Date(y, m, d - (day === 0 ? 13 : day + 6));
      const lastSun = new Date(y, m, d - (day === 0 ? 7 : day));
      return { from: fmt(lastMon), to: fmt(lastSun) };
    }
    case 'this_month':
      return { from: fmt(new Date(y, m, 1)), to: today };
    case 'last_month':
      return {
        from: fmt(new Date(y, m - 1, 1)),
        to:   fmt(new Date(y, m, 0)),
      };
    case 'last_3_months':
      return { from: fmt(new Date(y, m - 3, d)), to: today };
    case 'this_year':
      return { from: fmt(new Date(y, 0, 1)), to: today };
    case 'custom':
      return { from: currentFrom, to: currentTo };
    default:
      return { from: today, to: today };
  }
}

export default function DateFilterBar({
  value,
  onChange,
  onExport,
  exportLabel = 'Export Excel',
  extra = null,
}) {
  const setPreset = (preset) => {
    const r = presetRange(preset, value.from, value.to);
    onChange({ preset, from: r.from, to: r.to });
  };

  return (
    <Paper variant="outlined" sx={{
      px: 1.5, py: 1, mb: 1.5,
      display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 0.5 }}>
        <CalIcon sx={{ fontSize: 16, color: '#64748b' }} />
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, color: '#64748b', textTransform: 'uppercase' }}>
          Period
        </Typography>
      </Box>

      {PRESETS.map(p => (
        <Button
          key={p.id}
          size="small"
          variant={value.preset === p.id ? 'contained' : 'outlined'}
          onClick={() => setPreset(p.id)}
          sx={{
            minWidth: 'auto', px: 1.25, py: 0.25,
            fontSize: 11.5, fontWeight: 600, textTransform: 'none',
            borderRadius: 1,
            ...(value.preset === p.id ? {
              bgcolor: '#8E44AD', color: 'white',
              '&:hover': { bgcolor: '#7d3c98' },
            } : {
              color: '#475569', borderColor: '#cbd5e1',
              '&:hover': { bgcolor: '#f8fafc' },
            }),
          }}
        >
          {p.label}
        </Button>
      ))}

      {value.preset === 'custom' ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
          <TextField
            size="small" type="date" value={value.from}
            onChange={(e) => onChange({ ...value, from: e.target.value })}
            sx={{ '& input': { fontSize: 12, py: 0.5 }, width: 140 }}
            InputLabelProps={{ shrink: true }}
          />
          <Typography sx={{ color: '#94a3b8' }}>→</Typography>
          <TextField
            size="small" type="date" value={value.to}
            onChange={(e) => onChange({ ...value, to: e.target.value })}
            sx={{ '& input': { fontSize: 12, py: 0.5 }, width: 140 }}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      ) : (
        <Typography sx={{
          ml: 1, fontSize: 11.5, color: '#475569',
          px: 1.25, py: 0.5, bgcolor: '#f1f5f9', borderRadius: 1,
          fontFamily: 'JetBrains Mono, monospace',
        }}>
          {value.from} → {value.to}
        </Typography>
      )}

      {extra}

      {onExport && (
        <Button
          size="small"
          variant="contained"
          startIcon={<ExportIcon sx={{ fontSize: 16 }} />}
          onClick={onExport}
          sx={{
            ml: 'auto',
            background: 'linear-gradient(90deg, #16a34a, #15803d)',
            color: 'white', fontWeight: 700, fontSize: 12, textTransform: 'none',
            px: 1.5, py: 0.5,
            '&:hover': { background: 'linear-gradient(90deg, #15803d, #14532d)' },
          }}
        >
          {exportLabel}
        </Button>
      )}
    </Paper>
  );
}

// Default initial value
export const DEFAULT_RANGE = (() => {
  const today = new Date();
  const y = today.getFullYear(), m = today.getMonth(), d = today.getDate();
  return {
    preset: 'last_3_months',
    from:   fmt(new Date(y, m - 3, d)),
    to:     fmt(today),
  };
})();
